import { useQuery } from "@tanstack/react-query";

import {
  movementsService,
  type IngredientMovementRow,
  type MovementUser,
  type PaginatedResponse,
  type StockMovementRow,
  type StockMovementsFilters,
  type WmsMovementRow,
} from "~/services/wms/movementsService";

// Query keys for cache management
const MOVEMENTS_QUERY_KEYS = {
  stockMovements: (filters: StockMovementsFilters, page: number, pageSize: number) => [
    "movements",
    "stock",
    filters,
    page,
    pageSize,
  ],
  wmsMovements: (filters: StockMovementsFilters) => ["movements", "wms", filters],
  ingredientMovements: (filters: StockMovementsFilters) => [
    "movements",
    "ingredients",
    filters,
  ],
  movementUsers: () => ["movements", "users"],
  stockMovementTypes: () => ["movements", "stock-types"],
};

/**
 * Hook to get paginated stock movements
 * Uses server-side pagination for 60k+ rows
 */
export function useStockMovements(
  filters: StockMovementsFilters,
  page: number = 1,
  pageSize: number = 50
) {
  return useQuery<PaginatedResponse<StockMovementRow>, Error>({
    queryKey: MOVEMENTS_QUERY_KEYS.stockMovements(filters, page, pageSize),
    queryFn: () => movementsService.getStockMovements(filters, page, pageSize),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to get WMS movements with filters
 */
export function useWmsMovements(filters: StockMovementsFilters) {
  return useQuery<WmsMovementRow[], Error>({
    queryKey: MOVEMENTS_QUERY_KEYS.wmsMovements(filters),
    queryFn: () => movementsService.getWmsMovements(filters),
    staleTime: 30000,
  });
}

/**
 * Hook to get ingredient stock movements with filters
 */
export function useIngredientMovements(filters: StockMovementsFilters) {
  return useQuery<IngredientMovementRow[], Error>({
    queryKey: MOVEMENTS_QUERY_KEYS.ingredientMovements(filters),
    queryFn: () => movementsService.getIngredientMovements(filters),
    staleTime: 30000,
  });
}

/**
 * Hook to get users for filter dropdown
 */
export function useMovementUsers() {
  return useQuery<MovementUser[], Error>({
    queryKey: MOVEMENTS_QUERY_KEYS.movementUsers(),
    queryFn: () => movementsService.getMovementUsers(),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to get distinct stock movement types
 */
export function useStockMovementTypes() {
  return useQuery<string[], Error>({
    queryKey: MOVEMENTS_QUERY_KEYS.stockMovementTypes(),
    queryFn: () => movementsService.getStockMovementTypes(),
    staleTime: 300000, // 5 minutes
  });
}
